import React, { useState, useEffect } from "react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";

function SelectMajors({ formData, setFormData }) {
  // Initialize fields based on masterMajors array
  const [fields, setFields] = useState(() => {
    if (formData?.masterMajors?.length > 0) {
      return formData.masterMajors.map((major, index) => ({
        id: index,
        value: major,
        showButton: index === formData.masterMajors.length - 1
      }));
    }
    return [{ id: 0, value: '', showButton: true }];
  });

  // Update fields when formData changes
  useEffect(() => {
    if (formData?.masterMajors?.length > 0) {
      setFields(
        formData.masterMajors.map((major, index) => ({
          id: index,
          value: major,
          showButton: index === formData.masterMajors.length - 1
        }))
      );
    }
  }, [formData.masterMajors]);

  // Debug logging
  useEffect(() => {
    // console.log('masterMajors:', formData.masterMajors);
    // console.log('fields:', fields);
  }, [formData.masterMajors]);

  const handleInputChange = (id, value) => {
    setFields(prevFields => {
      const updatedFields = prevFields.map(field =>
        field.id === id ? { ...field, value } : field
      );
      
      // Update formData immediately after updating fields
      const majors = updatedFields.map(field => field.value)
        .filter(value => value.trim() !== '');
      
      setFormData(prev => ({
        ...prev,
        masterMajors: majors
      }));
      
      return updatedFields;
    });
  };

  const addNewField = (id) => {
    const newField = { id: Date.now(), value: '' };
    setFields((prevFields) =>
      prevFields
        .map((field) =>
          field.id === id ? { ...field, showButton: false } : field
        )
        .concat(newField)
    );
  };

  const removeField = (id) => {
    setFields(prevFields => {
      const updatedFields = prevFields.filter((field) => field.id !== id);
      
      // Update formData with the remaining fields
      const majors = updatedFields.map(field => field.value)
        .filter(value => value.trim() !== '');
      
      setFormData(prev => ({
        ...prev,
        masterMajors: majors
      }));

      return updatedFields;
    });
  };

  useGSAP(() => {
    const fieldElements = document.querySelectorAll(".relative");

    // Cleanup function to store all animations
    const animations = [];
    
    fieldElements.forEach((field) => {
      const input = field.querySelector("input");
      const label = field.querySelector("label");
      const pLabel = field.querySelector("p");

      if (!input || !label) {
        console.warn("Missing input or label in field:", field);
        return;
      }

      // Set initial state for fields with values
      if (input.value) {
        gsap.set(label, {
          top: -10,
          fontSize: "0.70rem",
          color: "#6b7280"
        });
      }

      // Focus event
      const focusHandler = () => {
        const anim = gsap.to(label, {
          top: -10,
          fontSize: "0.70rem",
          color: "#22c55e",
          duration: 0.25
        });
        animations.push(anim);
        
        gsap.to(input, { borderColor: "#22c55e", duration: 0.25 });
        if (pLabel) {
          pLabel.classList.remove("text-red-500");
          pLabel.classList.add("text-gray-500");
        }
      };

      // Input event
      const inputHandler = () => {
        if (input.value !== "") {
          const anim = gsap.to(label, { color: "#22c55e", duration: 0.25 });
          animations.push(anim);
          gsap.to(input, { borderColor: "#22c55e", duration: 0.25 });
        }
      };

      // Blur event
      const blurHandler = () => {
        if (input.value === "") {
          const anim = gsap.to(label, {
            top: 10,
            fontSize: "1rem",
            color: "#6b7280",
            duration: 0.25
          });
          animations.push(anim);
          gsap.to(input, { borderColor: "#d5d7db", duration: 0.25 });
        } else {
          gsap.to(input, { borderColor: "#d5d7db", duration: 0.25 });
          gsap.to(label, { color: "#6b7280", duration: 0.25 });
        }
      };

      // Add event listeners
      input.addEventListener("focus", focusHandler);
      input.addEventListener("input", inputHandler);
      input.addEventListener("blur", blurHandler);

      // Cleanup function
      return () => {
        // Kill all animations
        animations.forEach(anim => anim.kill());
        
        // Remove event listeners
        input.removeEventListener("focus", focusHandler);
        input.removeEventListener("input", inputHandler);
        input.removeEventListener("blur", blurHandler);
      };
    });
  }, [fields]);

  return (
    <>
      <hr className="border-t border-gray-300 mb-4" />
      <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">
        Select Majors In Masters
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {fields.map((field, index) => (
          <div className="relative flex items-center" key={field.id}>
            <div className="flex-grow">
              <label
                htmlFor={`major-${field.id}`}
                className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1"
              >
                {index === 0 ? "Enter Major" : "Enter Additional Major"}
              </label>
              <input
                id={`major-${field.id}`}
                type="text"
                className="w-full p-2 border rounded"
                value={field.value}
                onChange={(e) => handleInputChange(field.id, e.target.value)}
              />
              <p className="text-gray-500 text-xs mt-1">
                {index === 0
                  ? "Please enter Major"
                  : "Please enter Additional Major"}
              </p>
            </div>
            {/* Remove Button (only show if there are multiple fields) */}
            {fields.length > 1 && (
              <button
                className="ml-2 mb-5 bg-red-500 text-white p-2 rounded hover:bg-red-600"
                onClick={() => removeField(field.id)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20 12H4"
                  />
                </svg>
              </button>
            )}
            
            {/* Add Button */}
            {field.value.trim() !== "" && field.showButton !== false && (
              <button
                className="ml-2 mb-5 bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
                onClick={() => addNewField(field.id)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </button>
            )}
            
          </div>
        ))}
      </div>
    </>
  );
}

export default SelectMajors;